import { useQuery } from '@tanstack/react-query';
import { useApi, type AddressDto } from '@/shared/api';
import type { MapArea } from './ObjectLocationMap';

// Ohne jeden Treffer schaut die Karte aufs Land; hineinzoomen geht dann von Hand.
const GERMANY: MapArea = { latitude: 51.16, longitude: 10.45, zoom: 6 };
const COUNTRY_AREA: Record<string, MapArea> = {
  DE: GERMANY,
  AT: { latitude: 47.6, longitude: 14.14, zoom: 7 },
  CH: { latitude: 46.8, longitude: 8.23, zoom: 8 },
};

/**
 * Wohin die Karte ohne Pin schaut: in die Postleitzahl des Objekts. Dafür reicht irgendein
 * Treffer der Adresssuche dort — das Haus selbst hat der Kartendienst ja gerade nicht gefunden.
 */
export function useMapArea(address: AddressDto, enabled: boolean) {
  const api = useApi();
  const query = [address.postalCode, address.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const search = useQuery({
    queryKey: ['address-area', query, address.countryCode],
    queryFn: ({ signal }) =>
      api.addressVerification.search({ query, countryCode: address.countryCode }, signal),
    enabled: enabled && query.length >= 3,
    staleTime: Infinity,
    retry: false,
  });
  const hit = search.data
    ?.map((suggestion) => suggestion.address)
    .find((found) => typeof found.latitude === 'number' && typeof found.longitude === 'number');
  const nearby: MapArea | null =
    hit && typeof hit.latitude === 'number' && typeof hit.longitude === 'number'
      ? { latitude: hit.latitude, longitude: hit.longitude, zoom: 15 }
      : null;
  // Ein Fehler der Suche ist kein Grund, die Karte zu verweigern — dann eben das Land, und die
  // Karte sagt, dass man selbst hineinzoomen muss.
  return {
    area: nearby ?? COUNTRY_AREA[address.countryCode] ?? GERMANY,
    found: nearby !== null,
    loading: search.isLoading,
  };
}
